// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCommercialAgreements } from '@hooks/queries/commercialAgreements/useCommercialAgreements';
import useIsPackageAvailableFrom from '@hooks/queries/settings/useIsPackageAvailableFrom';
import React, { useState } from 'react';
import { v4 } from 'uuid';
import { Close } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { AsyncButton } from '../../components/Button/AsyncButton';
import DynamicListInput from '../../components/Form/DynamicListInput';
import TextInput from '../../components/Form/TextInput';
import { useParties } from '../../contexts/PartiesContext';
import { isInteger } from '../../utils/isInteger';
import { isPositiveDecimal } from '../../utils/isPostiveDecimal';
import { isValidFee } from '../../utils/validateFees';
import { CheckingCommercialAgreements } from './CheckingCommercialAgreements';
import { NoCommercialAgreement } from './NoCommercialAgreement';
import { PaidCredentialForm } from './PaidCredentialForm';
import { IClaim, ICredPayload, PaidFieldConfig } from './types';
import { useHandleFree } from './useHandleFree';
import { useHandlePaid } from './useHandlePaid';

interface OfferCredentialProps {
  open: boolean;
  onClose: () => void;
}
const emptyPayload: ICredPayload = {
  holder: '',
  id: '',
  description: '',
  holderActivityWeight: null,
};
const emptyClaims = [{ id: v4(), subject: '', property: '', value: '' }];
export const OfferCredential: React.FC<OfferCredentialProps> = ({ open, onClose }) => {
  const { party, operator } = useParties();
  const enableHolderActivityWeight = useIsPackageAvailableFrom(
    'utility-credential-app-v0',
    [party, operator],
    '0.3.0',
  );
  const [payload, setPayload] = React.useState<ICredPayload>(emptyPayload);
  const [index, setIndex] = useState(0);
  const isFree = index === 0;
  const commercialAgreements = useCommercialAgreements();
  const showCommercialAgreementsWarning =
    !isFree && !commercialAgreements.isLoading && !commercialAgreements.data?.length;
  const [claims, setClaims] = useState<IClaim[]>(emptyClaims);
  const showCheckingCommercialAgreements = !isFree && commercialAgreements.isLoading;
  const reset = () => {
    setPayload(emptyPayload);
    setClaims(emptyClaims);
    setIndex(0);
  };
  const handlePaid = useHandlePaid();
  const handleFree = useHandleFree();
  const meetsMinimumFee =
    !!payload.billingPeriodMinutes &&
    !!payload.feePerDayUsd &&
    isValidFee({
      billingPeriodMinutes: payload.billingPeriodMinutes,
      feePerDayUsd: payload.feePerDayUsd,
    });
  const disableFreeSubmit =
    !payload.holder ||
    !payload.id ||
    !payload.description ||
    !claims.length ||
    claims.some(({ subject, property, value }) => !subject || !property || !value);
  const disablePaidSubmit =
    disableFreeSubmit ||
    payload.holder === party ||
    !payload.billingPeriodMinutes ||
    !payload.depositInitialAmountUsd ||
    !payload.depositTargetAmountUsd ||
    (enableHolderActivityWeight.data ? !payload.holderActivityWeight : false) ||
    !meetsMinimumFee ||
    commercialAgreements.isLoading ||
    !commercialAgreements.data?.length;
  const disableSubmit = isFree ? disableFreeSubmit : disablePaidSubmit;
  const feeErrorMessage =
    'Either Fee per day, or Billing period is too small. Please adjust either to a larger value.';
  const buttonLabel = isFree ? 'Offer free credential' : 'Offer paid credential';
  const holderError =
    !isFree && party === payload.holder ? 'Holder party ID cannot be your own.' : undefined;
  const displayFeeError =
    !!payload?.feePerDayUsd && !!payload?.billingPeriodMinutes && !meetsMinimumFee;
  const commonFields: { name: keyof ICredPayload; helperText?: string }[] = [
    {
      name: 'holder',
      helperText: holderError,
    },
    { name: 'id' },
    { name: 'description' },
  ];
  const paidFields: PaidFieldConfig[] = [
    { label: 'Fee per day', name: 'feePerDayUsd', adornment: 'USD', type: 'decimal' },
    {
      name: 'billingPeriodMinutes',
      label: 'Billing Period',
      adornment: 'Minutes',
      helperText: displayFeeError ? feeErrorMessage : undefined,
      type: 'integer',
    },
    {
      label: 'Credential Fees Reserve Amount',
      name: 'depositInitialAmountUsd',
      adornment: 'USD',
      type: 'decimal',
    },
    {
      label: 'Credential Fees Target Reserve Amount',
      name: 'depositTargetAmountUsd',
      adornment: 'USD',
      type: 'decimal',
    },
    ...(enableHolderActivityWeight.data
      ? [
          {
            label: 'Holder Activity Weight',
            name: 'holderActivityWeight' as keyof ICredPayload,
            adornment: 'between 0 and 1',
            type: 'decimal' as const,
          },
        ]
      : []),
  ];
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    if (newValue === 0) {
      setPayload((p) => ({
        ...p,
        feePerDay: '',
        billingPeriodMinutes: '',
        depositInitialAmountUsd: '',
        depositTargetAmountUsd: '',
        holderActivityWeight: enableHolderActivityWeight.data ? '' : null,
      }));
    }
    setIndex(newValue);
  };

  React.useEffect(() => {
    reset();
  }, [open]);

  const onFreeCredentialFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPayload((p) => ({ ...p, [name]: value }));
  };
  const onPaidCredentialFormChange = (e: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const { name, value } = e.target;
    if (value === '') {
      setPayload((p) => ({ ...p, [name]: value }));
    }
    if (type === 'integer' && isInteger(value)) {
      setPayload((p) => ({ ...p, [name]: value }));
    }
    if (type === 'decimal' && isPositiveDecimal(value)) {
      setPayload((p) => ({ ...p, [name]: value }));
    }
  };
  const onSubmit = async () => {
    if (isFree) {
      await handleFree(payload, claims);
    } else {
      await handlePaid(payload, claims);
    }
    onClose();
  };

  const createNewClaim = (): IClaim => ({
    id: v4(),
    subject: '',
    property: '',
    value: '',
  });

  const renderClaim = (claim: IClaim, updateClaim: (updatedClaim: IClaim) => void) => (
    <Box>
      <TextInput
        label="Subject"
        value={claim.subject}
        setValue={(value) => updateClaim({ ...claim, subject: value as string })}
        required
      />
      <TextInput
        label="Property"
        value={claim.property}
        setValue={(value) => updateClaim({ ...claim, property: value as string })}
        required
      />
      <TextInput
        label="Value"
        value={claim.value}
        setValue={(value) => updateClaim({ ...claim, value: value as string })}
        required
      />
    </Box>
  );
  return (
    <Dialog scroll="paper" open={open} onClose={onClose}>
      <DialogTitle sx={{ pb: 0 }}>
        <Box
          sx={{
            justifyContent: 'center',
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <Typography variant="body1" fontWeight="bold">
            Offer Credentials
          </Typography>
          <Box sx={{ position: 'absolute', right: 0, pr: 2 }}>
            <IconButton onClick={onClose} size="small">
              <Close color="action" fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        <Tabs onChange={handleTabChange} variant="fullWidth" value={index}>
          <Tab sx={{ textTransform: 'capitalize' }} value={0} label="Free" />
          <Tab sx={{ textTransform: 'capitalize' }} value={1} label="Paid" />
        </Tabs>
      </DialogTitle>

      <DialogContent sx={{ height: 500, width: 400 }} dividers>
        {showCheckingCommercialAgreements && <CheckingCommercialAgreements />}
        {showCommercialAgreementsWarning && <NoCommercialAgreement />}
        {commonFields.map((f) => (
          <TextField
            key={f.name}
            helperText={f.helperText}
            error={!!f.helperText}
            size="small"
            placeholder={f.name}
            sx={{ mb: 1 }}
            value={payload[f.name]}
            name={f.name}
            fullWidth
            onChange={onFreeCredentialFormChange}
          />
        ))}

        <DynamicListInput<IClaim>
          title="Claims"
          list={claims}
          setList={setClaims}
          createNewItem={createNewClaim}
          renderItem={renderClaim}
        />
        {!!commercialAgreements.data?.length && index === 1 && (
          <PaidCredentialForm
            payload={payload}
            paidFields={paidFields}
            onChange={onPaidCredentialFormChange}
          />
        )}
      </DialogContent>

      <DialogActions>
        <AsyncButton
          disabled={disableSubmit}
          enableDefaultPadding
          onClick={onSubmit}
          color={isFree ? 'primary' : 'warning'}
          fullWidth
        >
          {buttonLabel}
        </AsyncButton>
      </DialogActions>
    </Dialog>
  );
};
